import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, LogOut, RefreshCw, Users, X } from 'lucide-react';
import { api } from '../api';
import { Brand, Button, Card, Empty, GameIcon, Notice, Shell } from '../components';
import { POWERUPS_ENABLED, teamAccentColor } from '../../shared/constants';
import { BoostBanner, PeekPanel, PowerupsPanel } from '../powerups';
import { referenceImageUrl } from '../promptDeck';
import type { PowerupState } from '../powerups';

interface Me {
  id: string;
  nickname: string;
  avatar: string;
  teamId: string | null;
  teamName: string | null;
  teamColor: string | null;
  gameName: string;
  joinCode: string;
  status: string;
  leaderboardVisible: boolean;
  soloMode: boolean;
  teamScore: number | null;
  teammates: { id: string; nickname: string; avatar: string }[];
  announcementMessage?: string | null;
  announcementExpiresAt?: string | null;
}
interface Task {
  id: string;
  name: string;
  type: string;
  description: string;
  points: number;
  individualPoints: number;
  status?: string;
  requiresApproval: boolean;
  requiresTeam: boolean;
  answerMode?: string;
  answerOptions?: string[] | null;
  submissionStatus?: string | null;
  /** The player's own photo for this task, if they submitted one. */
  mediaId?: string | null;
  category?: string | null;
  /** A picture to recreate, from the photo deck. */
  referenceImage?: string | null;
}

export function PlayerPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [me, setMe] = useState<Me | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [powerupState, setPowerupState] = useState<PowerupState | null>(null);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dismissedAnnouncement, setDismissedAnnouncement] = useState<string | null>(null);
  const scanOnce = useRef(false);
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [message]);
  async function load() {
    try {
      const [profile, list, powerups] = await Promise.all([
        api<Me>('/api/player/me'),
        api<{ tasks: Task[] }>('/api/player/tasks'),
        POWERUPS_ENABLED ? api<PowerupState>('/api/player/powerups') : null,
      ]);
      setMe(profile);
      setTasks(list.tasks);
      setPowerupState(powerups);
    } catch {
      const next = token ? `/scan/${token}` : '/play';
      if (token) localStorage.setItem('rave_next', next);
      navigate(`/?next=${encodeURIComponent(next)}`);
    }
  }
  useEffect(() => {
    void load();
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!me?.announcementExpiresAt) return;
    const raw = me.announcementExpiresAt;
    const normalized =
      raw.includes('T') || raw.endsWith('Z')
        ? raw.endsWith('Z')
          ? raw
          : `${raw}Z`
        : `${raw.trim().replace(' ', 'T')}Z`;
    const expires = new Date(normalized).getTime();
    const wait = expires - Date.now();
    if (Number.isNaN(expires)) return;
    if (wait <= 0) {
      setMe((current) =>
        current ? { ...current, announcementMessage: null, announcementExpiresAt: null } : current,
      );
      void load();
      return;
    }
    const timer = setTimeout(() => {
      setMe((current) =>
        current ? { ...current, announcementMessage: null, announcementExpiresAt: null } : current,
      );
      void load();
    }, wait);
    return () => clearTimeout(timer);
  }, [me?.announcementExpiresAt, me?.announcementMessage]);
  useEffect(() => {
    if (token && !scanOnce.current && me) {
      scanOnce.current = true;
      void scan(token);
    }
  }, [token, me]);
  async function scan(value: string) {
    setBusy(true);
    try {
      const result = await api<{ message: string; penalty?: boolean }>(
        `/api/player/scan/${value}`,
        { method: 'POST' },
      );
      // Trap codes still count as scanned, but read as a warning rather than a win.
      setMessage({ type: result.penalty ? 'error' : 'success', text: result.message });
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }
  async function submit(task: Task, answer?: string, file?: File) {
    setBusy(true);
    try {
      let result: { message: string };
      if (file) {
        const form = new FormData();
        form.append('photo', file);
        result = await api(`/api/player/tasks/${task.id}/photo`, { method: 'POST', body: form });
      } else {
        result = await api(`/api/player/tasks/${task.id}/submit`, {
          method: 'POST',
          body: JSON.stringify({ answer }),
        });
      }
      setMessage({ type: 'success', text: result.message });
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }
  if (!me)
    return (
      <Shell>
        <div className="center">
          <RefreshCw className="spin" />
        </div>
      </Shell>
    );
  const waiting = !me.teamId || ['setup', 'registration'].includes(me.status);
  // Once play stops, challenges are a recap: the server already rejects new submissions.
  const over = ['ended', 'awards', 'archived'].includes(me.status);
  const paused = me.status === 'paused';
  const announcementKey = me.announcementMessage
    ? `${me.announcementMessage}\0${me.announcementExpiresAt ?? ''}`
    : null;
  const showAnnouncement = Boolean(announcementKey && announcementKey !== dismissedAnnouncement);
  const completedTasks = tasks.filter((task) => task.submissionStatus === 'approved');
  const activeTasks = tasks.filter((task) => task.submissionStatus !== 'approved');
  return (
    <Shell>
      <header className="topbar">
        <Brand compact />
        <button
          className="icon-button"
          onClick={async () => {
            await api('/api/player/logout', { method: 'POST' });
            navigate('/');
          }}
        >
          <LogOut size={20} />
        </button>
      </header>
      {showAnnouncement && me.announcementMessage && (
        <div className="player-announcement-banner" role="status">
          <p>{me.announcementMessage}</p>
          <button
            type="button"
            className="player-announcement-close"
            aria-label="Dismiss announcement"
            onClick={() => setDismissedAnnouncement(announcementKey)}
          >
            <X size={18} />
          </button>
        </div>
      )}
      <div className="player-content">
        {message && (
          <Notice type={message.type} onClose={() => setMessage(null)}>
            {message.text}
          </Notice>
        )}
        {waiting ? (
          <Card className="waiting">
            <div className="big-avatar">
              <GameIcon name={me.avatar} />
            </div>
            <h1>You're in, {me.nickname}!</h1>
            <p>
              {me.soloMode
                ? 'The party starts soon. Keep this screen open.'
                : 'The host is preparing teams. Keep this screen open.'}
            </p>
            <span className="status-dot">Waiting for the hunt to begin</span>
          </Card>
        ) : (
          <>
            {me.soloMode ? (
              <Card
                className="team-card"
                style={
                  {
                    '--team-color': me.teamColor ?? '#7c3aed',
                    '--team-accent': teamAccentColor(me.teamColor ?? '#7c3aed'),
                  } as React.CSSProperties
                }
              >
                <div className="team-card-header">
                  <div className="solo-card-player">
                    <span className="solo-card-avatar" aria-hidden>
                      <GameIcon name={me.avatar} />
                    </span>
                    <div>
                      <small>Playing solo</small>
                      <h1>{me.nickname}</h1>
                    </div>
                  </div>
                  {me.teamScore !== null ? (
                    <div className="team-card-score">
                      <small>Your score</small>
                      <strong>{me.teamScore}</strong>
                    </div>
                  ) : null}
                </div>
              </Card>
            ) : (
              <Card
                className="team-card"
                style={
                  {
                    '--team-color': me.teamColor ?? '#7c3aed',
                    '--team-accent': teamAccentColor(me.teamColor ?? '#7c3aed'),
                  } as React.CSSProperties
                }
              >
                <div className="team-card-header">
                  <div>
                    <small>Your Team</small>
                    <h1>{me.teamName}</h1>
                  </div>
                  {me.teamScore !== null ? (
                    <div className="team-card-score">
                      <small>Team score</small>
                      <strong>{me.teamScore}</strong>
                    </div>
                  ) : null}
                </div>
                <div className="team-card-members">
                  <small>Team Members</small>
                  {me.teammates?.length ? (
                    <ul className="team-roster">
                      {me.teammates.map((mate) => (
                        <li
                          key={mate.id}
                          className={mate.id === me.id ? 'team-roster-item--you' : undefined}
                        >
                          <span aria-hidden>
                            <GameIcon name={mate.avatar} />
                          </span>
                          <strong>{mate.nickname}</strong>
                          {mate.id === me.id ? <em>You</em> : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="team-members-empty">No teammates yet.</p>
                  )}
                </div>
              </Card>
            )}
            {POWERUPS_ENABLED && powerupState && !over ? (
              <>
                <BoostBanner expiresAt={powerupState.boostExpiresAt} />
                <PeekPanel expiresAt={powerupState.peekExpiresAt} />
                <PowerupsPanel
                  state={powerupState}
                  onUsed={(text, ok) => {
                    setMessage({ type: ok ? 'success' : 'error', text });
                    void load();
                  }}
                />
              </>
            ) : null}
            {over ? (
              <Card className="game-over-card card--loud">
                <h2>{me.status === 'awards' ? 'Awards time!' : 'The hunt is over'}</h2>
                <p>
                  {me.status === 'awards'
                    ? 'Eyes on the party display to see who won.'
                    : 'No more challenges can be completed. Watch the party display for the awards.'}
                </p>
              </Card>
            ) : (
              <>
                {paused ? (
                  <Notice type="info">
                    The game is paused. Challenges will reopen when the host resumes.
                  </Notice>
                ) : null}
                <h2>Active Challenges</h2>
                <div className="task-grid">
                  {activeTasks.length === 0 ? (
                    <Empty>
                      {completedTasks.length
                        ? 'All caught up — new challenges drop through the night.'
                        : 'No challenges are out yet. New ones drop through the night.'}
                    </Empty>
                  ) : (
                    activeTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        busy={busy}
                        locked={paused}
                        onSubmit={submit}
                      />
                    ))
                  )}
                </div>
              </>
            )}
            {completedTasks.length > 0 && (
              <>
                <h2>Completed Challenges</h2>
                <div className="task-grid">
                  {completedTasks.map((task) => (
                    <TaskCard key={task.id} task={task} busy={busy} onSubmit={submit} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

function TaskCard({
  task,
  busy,
  locked = false,
  onSubmit,
}: {
  task: Task;
  busy: boolean;
  /** Paused games keep challenges visible but not submittable. */
  locked?: boolean;
  onSubmit: (task: Task, answer?: string, file?: File) => void;
}) {
  const [answer, setAnswer] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const approved = task.submissionStatus === 'approved';
  const submitted = Boolean(task.submissionStatus);
  const closed = task.status === 'closed';
  const reference = referenceImageUrl(task.referenceImage);
  return (
    <Card className={`task-card ${approved ? 'task-card--done' : ''}`}>
      <div className="task-meta">
        <span>{task.category ?? task.type}</span>
        <strong className={task.points < 0 ? 'task-points-negative' : undefined}>
          {task.points} pts
        </strong>
      </div>
      <h3>{task.name}</h3>
      <p>{task.description}</p>
      {reference && !task.mediaId ? (
        <figure className="task-reference-figure">
          <img className="task-reference" src={reference} alt={`Recreate this: ${task.name}`} />
          <figcaption>Recreate this</figcaption>
        </figure>
      ) : null}
      {task.mediaId ? (
        <img
          className="task-photo"
          src={`/media/${task.mediaId}`}
          alt={`Your photo for ${task.name}`}
          loading="lazy"
        />
      ) : null}
      {approved ? null : submitted ? (
        <span className={`pill pill--${task.submissionStatus}`}>{task.submissionStatus}</span>
      ) : closed ? (
        <span className="pill pill--closed">Closed</span>
      ) : task.type === 'photo' ? (
        <>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onSubmit(task, undefined, file);
            }}
          />
          <Button busy={busy} disabled={locked} onClick={() => input.current?.click()}>
            <Camera size={18} />
            Take or Choose Photo
          </Button>
        </>
      ) : ['trivia', 'manual'].includes(task.type) ? (
        <div
          className={
            task.type === 'trivia' && task.answerMode === 'multiple_choice'
              ? 'stack'
              : 'inline-form'
          }
        >
          {task.type === 'trivia' && task.answerMode === 'multiple_choice' ? (
            <div className="choice-list">
              {(task.answerOptions ?? []).map((option) => (
                <Button
                  key={option}
                  variant={answer === option ? 'primary' : 'secondary'}
                  busy={busy}
                  disabled={locked}
                  onClick={() => onSubmit(task, option)}
                >
                  {option}
                </Button>
              ))}
            </div>
          ) : (
            <>
              {task.type === 'trivia' && (
                <input
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Your answer"
                />
              )}
              <Button busy={busy} disabled={locked} onClick={() => onSubmit(task, answer)}>
                {task.type === 'manual' ? <Users size={18} /> : null}Submit
              </Button>
            </>
          )}
        </div>
      ) : null}
    </Card>
  );
}
